import React, { memo, useState } from "react";
import { Typography, Space, Tag, Button } from "antd";
import { ColumnsType } from "antd/es/table";
import { GetRowKey } from "antd/es/table/interface";
import { TableProps } from "antd/lib";
import useScreen from "@lib/hooks/useScreen";
import {
  StyledMobileTable,
  StyledMobileTableCard,
  StyledMobileTableCol,
  StyledMobileTableEmpty,
  StyledMobileTableLabelText,
  StyledMobileTableRow,
  StyledMobileTableSpin,
  StyledMobileTableSpinDiv,
  StyledMobileTableValueText,
} from "@lib/components/MiniComponents/Table/index.styled";
import { useIntl } from "react-intl";
import { TableHeaderFilter } from "@lib/types/Api";

const { Text } = Typography;

export type AppMobileTableExtraProps = {
  initialVisibleColumns?: number;
  showMoreIcon?: boolean;
  maxHeight?: string;
  hiddenColumns?: string[];
};

export type AppMobileTableProps<T> = {
  dataSource: T[];
  columns: ColumnsType<T>;
  loading: boolean;
  rowKey: string | keyof T | GetRowKey<T>;
  onRow?: (record: T, index: number) => React.HTMLAttributes<HTMLElement>;
  striped?: boolean;
  filters?: TableHeaderFilter[];
  handleRemoveFilters?: (keys: string[]) => void;
  onChange?: TableProps<T>["onChange"];
} & AppMobileTableExtraProps;

const AppMobileTable = <T extends Record<string, any>>(
  props: AppMobileTableProps<T>,
) => {
  const {
    dataSource,
    columns,
    loading,
    rowKey,
    striped = true,
    onRow,
    initialVisibleColumns = 4,
    showMoreIcon = true,
    hiddenColumns = [],
    maxHeight,
  } = props;
  const { messages } = useIntl();
  const { isMobile } = useScreen();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const renderValue = (column: any, record: T) => {
    if (column.render) {
      return column.render(record[column.dataIndex], record, column.dataIndex);
    }

    const value = record[column.dataIndex];

    if (typeof value === "boolean") {
      return <Tag color={value ? "green" : "red"}>{value ? "Yes" : "No"}</Tag>;
    }

    if (typeof value === "string" && value.length > 50) {
      return <Text ellipsis={{ tooltip: value }}>{value}</Text>;
    }

    return <StyledMobileTableValueText>{value}</StyledMobileTableValueText>;
  };

  const getRowKey = (record: T) => {
    if (typeof rowKey === "string") {
      return record[rowKey];
    }
    if (typeof rowKey === "function") {
      return rowKey(record);
    }
    return record.id || record.key;
  };

  if (!isMobile) {
    return null;
  }

  return (
    <StyledMobileTable
      className="mobile-table"
      style={{ maxHeight: maxHeight }}
    >
      {loading && (
        <StyledMobileTableSpinDiv>
          <StyledMobileTableSpin />
        </StyledMobileTableSpinDiv>
      )}
      {(!dataSource || dataSource?.length === 0) && <StyledMobileTableEmpty />}
      {dataSource &&
        dataSource?.map((record, index) => {
          const key = getRowKey(record);
          const rowProps = onRow ? onRow(record, index) : {};
          const isEven = index % 2 === 0;
          const isExpanded = expandedRows.has(key);
          const shouldShowToggleButton =
            columns && columns.length > initialVisibleColumns;
          const visibleColumns = isExpanded
            ? columns || []
            : (columns || []).slice(0, initialVisibleColumns);

          const toggleRow = () => {
            const newExpandedRows = new Set(expandedRows);
            if (isExpanded) {
              newExpandedRows.delete(key);
            } else {
              newExpandedRows.add(key);
            }
            setExpandedRows(newExpandedRows);
          };

          return (
            <StyledMobileTableCard
              key={key}
              $striped={striped && isEven}
              size="small"
              {...rowProps}
            >
              {visibleColumns
                .filter(
                  (column: any) => !hiddenColumns?.includes(column.dataIndex),
                )
                .map((column: any) => {
                  // if (column.hidden || column.fixed === "right") return null;

                  return (
                    <StyledMobileTableRow key={column.key || column.dataIndex}>
                      <StyledMobileTableCol
                        span={10}
                        style={{ padding: "0 5px", marginBottom: "5px" }}
                      >
                        <StyledMobileTableLabelText ellipsis>
                          {column.title}
                        </StyledMobileTableLabelText>
                      </StyledMobileTableCol>
                      <StyledMobileTableCol
                        span={14}
                        style={{ padding: "0 5px", marginBottom: "5px" }}
                      >
                        <Space direction="vertical" style={{ width: "100%" }}>
                          {renderValue(column, record)}
                        </Space>
                      </StyledMobileTableCol>
                    </StyledMobileTableRow>
                  );
                })}

              {/* Action buttons for fixed right columns */}
              {/* {visibleColumns
                .filter((col: any) => col.fixed === "right" && col.render)
                .map((column: any) => (
                  <StyledMobileTableRow
                    key={`action-${column.key || column.dataIndex}`}
                  >
                    <StyledMobileTableCol
                      span={24}
                      style={{ justifyContent: "flex-end" }}
                    >
                      {column.render?.(null, record, column.dataIndex)}
                    </StyledMobileTableCol>
                  </StyledMobileTableRow>
                ))} */}

              {/* Toggle button for each row */}
              {shouldShowToggleButton && showMoreIcon && (
                <StyledMobileTableRow>
                  <StyledMobileTableCol
                    span={24}
                    style={{
                      justifyContent: "center",
                      alignItems: "center",
                      marginBottom: "0",
                    }}
                  >
                    <Button
                      type="text"
                      onClick={toggleRow}
                      style={{
                        color: "#1890ff",
                        border: "1px dashed #d9d9d9",
                        borderRadius: "6px",
                        padding: "4px 12px",
                        fontSize: "12px",
                      }}
                    >
                      {isExpanded ? "▲" : "▼"}{" "}
                      {isExpanded
                        ? (messages["common.showLess"] as string)
                        : (messages["common.showMore"] as string)}
                    </Button>
                  </StyledMobileTableCol>
                </StyledMobileTableRow>
              )}
            </StyledMobileTableCard>
          );
        })}
    </StyledMobileTable>
  );
};

export default memo(AppMobileTable);
